<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Services
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Helpers
	import { get_gen_2_component_channel_structure } from '../../../../helpers/interface_helpers';
	import { real_id } from '../../../../helpers/interface_helpers';
	import { fetchVialColor } from './Cury/helpers/cury_fetch_vial_color';
	import { profiles } from './Cury/helpers/cury_profiles';
	import { setCuryMode, getCuryModeState } from '../../../../helpers/cury_mode_helpers';
	import { deleteExistingSchedulesForDevice } from './Cury/helpers/cury_schedules';
	import { disableUserAutomations as disableUserAutomationsHelper } from './Cury/helpers/cury_device_actions';
	//Design system components
	import { Button, Text, Alert, Toggle } from '@Shelly/shelly-design-system';
	//Svelte hooks
	import { onMount } from 'svelte';
	//Components
	import CuryProfilesControls from './Cury/components/CuryProfilesControls.svelte';
	import CurySchedulePreview from './Cury/components/CurySchedulePreview.svelte';
	import AwayModeActiveAlert from 'src/components/Alerts/AwayModeActiveAlert.svelte';
	//Types
	import type { Device } from 'src/types/Device';
	import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
	import type { CuryProfile, VialColors, CuryMode } from 'src/types/Cury';

	export let device: Device;

	const settings: DeviceSettingsG2 = devices_settings.getByRealId(device.id);
	const component: string = get_gen_2_component_channel_structure(device);

	// Get current status to read the mode
	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;

	// profiles moved to helper

	// Category options
	const categories: string[] = ['home', 'office'];
	const categoryOptionsI18n: string[] = [
		$format('label_home', { default: 'Home' }),
		$format('label_office', { default: 'Office' }),
	];

	let selectedCategory: string = 'home';
	let selectedMode: CuryMode = null;
	let cacheMode: CuryMode = null;
	let loading: boolean = false;
	let initialized: boolean = false;
	let profilesEnabled: boolean = false;
	let cacheProfilesEnabled: boolean = false;
	let vialColorSlot: VialColors = {
		left: 'var(--color-muted)',
		right: 'var(--color-muted)',
	};

	// Check if away mode is active
	$: awayModeActive = status?.[component]?.away_mode || false;

	// Auto-select first mode when profiles are enabled
	$: if (profilesEnabled && !selectedMode && modeOptions.length > 0) {
		selectedMode = modeOptions[0] as CuryMode;
	}

	// Initialize selected mode from status ONLY on first load
	$: if (status && !initialized) {
		const modeState = getCuryModeState(status, component);
		if (modeState.mode) {
			selectedMode = modeState.mode;
			cacheMode = modeState.mode;
			// Update category based on mode
			const profile = profiles[modeState.mode];
			if (profile) {
				selectedCategory = profile.category;
			}
			profilesEnabled = true;
			cacheProfilesEnabled = true;
			initialized = true;
		}
	}

	let categoryModes: Record<string, CuryProfile> = {};
	let modeOptions: string[] = [];
	let modeOptionsI18n: string[] = [];

	// Update modes when category changes
	$: {
		categoryModes = Object.entries(profiles)
			.filter(([_, profile]) => profile.category === selectedCategory)
			.reduce((acc, [key, profile]) => {
				acc[key] = profile;
				return acc;
			}, {}) as Record<string, CuryProfile>;

		modeOptions = Object.keys(categoryModes);
		modeOptionsI18n = Object.values(categoryModes).map((profile: CuryProfile) =>
			$format(`label_${profile.mode_id}`, { default: profile.label }),
		);
	}

	const changeCategory = (categoryI18n: string) => {
		const categoryIndex = categoryOptionsI18n.indexOf(categoryI18n);
		if (categoryIndex === -1) return;
		selectedCategory = categories[categoryIndex];

		// Reset to first mode of new category if current mode doesn't match
		if (
			!selectedMode ||
			!profiles[selectedMode] ||
			profiles[selectedMode].category !== selectedCategory
		) {
			const newCategoryModes = Object.entries(profiles).filter(
				([_, profile]) => profile.category === selectedCategory,
			);
			if (newCategoryModes.length > 0) {
				selectedMode = newCategoryModes[0][0] as CuryMode;
			}
		}
	};

	const changeMode = (modeI18n: string) => {
		const modeIndex = modeOptionsI18n.indexOf(modeI18n);
		if (modeIndex === -1) return;
		selectedMode = modeOptions[modeIndex] as CuryMode;
	};

	const save = async (): Promise<void> => {
		if (cacheMode === selectedMode && cacheProfilesEnabled === profilesEnabled) {
			return;
		}

		loading = true;

		try {
			if (!profilesEnabled) {
				// Disable predefined profiles: set mode to null, delete schedules, re-enable automations
				await setCuryMode(device, null);
				await deleteExistingSchedulesForDevice(device);
				await gen2Request(device, 'Cury.SetConfig', {
					id: 0,
					config: {
						automations: { enabled: true },
						profiles_enabled: false,
						auto_switching: false,
					},
				});
				cacheMode = selectedMode = null;
				cacheProfilesEnabled = profilesEnabled;
				return;
			}

			// Enabling profiles: mode should be auto-selected by reactive statement
			const res: any = await setCuryMode(device, selectedMode);

			if (res?.isok) {
				cacheMode = selectedMode;
				cacheProfilesEnabled = profilesEnabled;
				await disableUserAutomationsHelper(device);
			} else {
				selectedMode = cacheMode;
				throw new Error('Failed to set cury mode');
			}
		} catch (err) {
			console.log('Failed to save mode/profiles state:', err);
			selectedMode = cacheMode;
			profilesEnabled = cacheProfilesEnabled;
		} finally {
			loading = false;
		}
	};

	onMount(async () => {
		// Fetch vial colors for display
		const colors = await fetchVialColor(device);
		vialColorSlot = colors;

		// Initialize mode from status if not set
		if (!selectedMode && status) {
			const modeState = getCuryModeState(status, component);
			if (modeState.mode) {
				selectedMode = modeState.mode;
				cacheMode = modeState.mode;
				const profile = profiles[modeState.mode];
				if (profile) {
					selectedCategory = profile.category;
				}
			}
		}
	});
</script>

{#if awayModeActive}
	<AwayModeActiveAlert {device} />
{:else}
	<Alert
		class="mb-s"
		type="info"
		heading={$format('title_heading_modes_of_operation', {
			default: 'Modes of Operation',
		})}
		isHeadingBold
		message={$format('description_modes_of_operation', {
			default:
				'Select a mode to automatically configure your Cury device with predefined schedules optimized for different room types. The device will automatically switch between slots according to the schedule. Changing modes will replace any previously configured schedules.',
		})}
	/>

	<Toggle id="toggle-enable-profiles" class="mb-s" bind:checked={profilesEnabled} size="m">
		{$format('label_enable', { default: 'Enable' })}
	</Toggle>

	{#if profilesEnabled}
		<Text styleType="body-small" class="mb-xs text-bold">
			{$format('label_current_mode', { default: 'Current Mode' })}:
			{#if selectedMode}
				{$format(`label_${selectedMode}`, { default: selectedMode.replace('_', ' ') })}
			{:else}
				{$format('label_no_mode_selected', { default: 'No mode selected' })}
			{/if}
		</Text>

		<CuryProfilesControls
			{categories}
			{categoryOptionsI18n}
			{selectedCategory}
			{changeCategory}
			profileOptionsI18n={modeOptionsI18n}
			profileOptions={modeOptions}
			selectedProfile={selectedMode || ''}
			changeProfile={changeMode}
		/>

		{#if selectedMode && profiles[selectedMode]}
			<CurySchedulePreview {vialColorSlot} profile={profiles[selectedMode]} />
		{/if}
	{:else}
		<Alert type="info" class="mb-s">
			<Text styleType="body-small">
				{$format('label_profiles_disabled', {
					default:
						'Profiles are disabled. You can now use manual schedules and user automations. Auto-switching is turned off.',
				})}
			</Text>
		</Alert>
	{/if}

	<Button {loading} size="m" width="full" on:click={save}>
		{$format('button_save')}
	</Button>
{/if}
